import { Check, ExternalLink } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { StepStatus } from './checkoutData'

// Mock: how long the provider takes to confirm payment, then how long "Payment complete" shows before the step closes.
const CONFIRM_MS = 4000
const COMPLETE_HOLD_MS = 1500

/** Payment happens on the provider's own page; this step only links out and reports back. */
export function CarrierPaymentStep({
  carrierName,
  startDate,
  status,
  onStatusChange,
}: {
  carrierName: string
  startDate: string
  status?: StepStatus
  onStatusChange: (status: StepStatus) => void
}) {
  const [isConfirmed, setIsConfirmed] = useState(false)

  useEffect(() => {
    if (status !== StepStatus.PENDING) return
    setIsConfirmed(false)
    const confirm = setTimeout(() => setIsConfirmed(true), CONFIRM_MS)
    const finish = setTimeout(() => onStatusChange(StepStatus.SUCCESS), CONFIRM_MS + COMPLETE_HOLD_MS)
    return () => {
      clearTimeout(confirm)
      clearTimeout(finish)
    }
  }, [status])

  if (status === StepStatus.SUCCESS) {
    return (
      <div className="mt-3 flex flex-col items-start gap-1 lg:max-w-[400px]">
        <p className="text-ink-500 text-sm">Your coverage starts on {startDate}.</p>
      </div>
    )
  }

  const isPending = status === StepStatus.PENDING

  return (
    <div className="mt-3 flex flex-col items-start gap-3 lg:max-w-[400px]">
      {status === StepStatus.FAILURE ? (
        <Banner variant="error" small>
          {carrierName} couldn&apos;t process your payment. Try again, or use a different payment method.
        </Banner>
      ) : (
        <p className="text-ink-500 text-sm">
          You&apos;ll pay on {carrierName}&apos;s secure payment page. We&apos;ll update this step once{' '}
          {carrierName} confirms it.
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        {/* Mock: the real button opens the payment URL the provider generates for this policy. */}
        <Button
          variant={isPending ? 'outline' : 'default'}
          disabled={isConfirmed}
          onClick={() => onStatusChange(StepStatus.PENDING)}
        >
          {status === StepStatus.FAILURE ? 'Try again' : `Go to ${carrierName} payments`}
          <ExternalLink />
        </Button>
        {isPending &&
          (isConfirmed ? (
            <Badge key="complete" variant="success" className="animate-in fade-in zoom-in-95 duration-300">
              <Check />
              Payment complete
            </Badge>
          ) : (
            <Badge key="pending" variant="secondary" className="animate-in fade-in duration-300">
              <Spinner className="size-3" />
              Pending
            </Badge>
          ))}
      </div>
    </div>
  )
}
